import React, {useState, useEffect, useRef} from 'react';
import {useSelector} from "react-redux";
import loadingGif from '../assets/images/loader.gif';

/**
 * IMP-804 - In-browser viewer for the merged "bulk" admission documents PDF.
 *
 * The bulk PDF endpoint (/admission/{id}/pdf) returns the file with a
 * Content-Disposition: attachment header, which would force a download if the
 * URL were opened directly in an iframe. To display it inline we fetch the
 * response as a blob and render it through an object URL, which also lets us
 * trigger printing from the embedded viewer.
 */
const AdmissionBulkDocumentsPdf = ({admissionId, width = 1000, height = 800, action: actionProp, downloadName, title = "Patient Admission Documents", actions = null}) => {
    const idToken = useSelector(state => state.auth.idToken);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [filename, setFilename] = useState(downloadName || `admission_${admissionId}.pdf`);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const iframeRef = useRef(null);
    // IMP-767 - the endpoint is overridable so the same blob+iframe+print viewer can render
    // any inline PDF (e.g. the generated surgical consent form), not only the bulk documents.
    const action = actionProp || `${process.env.REACT_APP_API_PATH}/admission/${admissionId}/pdf`;

    useEffect(() => {
        let objectUrl = null;
        let active = true;
        setLoading(true);
        setError(null);

        fetch(action, {
            method: 'POST',
            headers: {'Content-Type': 'application/x-www-form-urlencoded'},
            body: `token=${encodeURIComponent(idToken)}`,
        })
            .then(response => {
                if (!response.ok) {
                    throw new Error(`Failed to load document (${response.status})`);
                }
                const disposition = response.headers.get('Content-Disposition') || '';
                const match = disposition.match(/filename="?([^";]+)"?/i);
                const name = match && match[1] ? match[1].trim() : (downloadName || `admission_${admissionId}.pdf`);
                return response.blob().then(blob => ({blob, name}));
            })
            .then(({blob, name}) => {
                if (!active) return;
                objectUrl = URL.createObjectURL(blob);
                setPdfUrl(objectUrl);
                setFilename(name);
            })
            .catch(() => {
                if (active) setError('Failed to load document. Please try again.');
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        return () => {
            active = false;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [action, idToken, admissionId, downloadName]);

    const printPdf = () => {
        const iframe = iframeRef.current;
        if (iframe && iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
        }
    };

    const downloadPdf = () => {
        if (!pdfUrl) return;
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div>
            <div style={{marginBottom: '8px'}}>
                {/* Optional caller-supplied controls rendered before Print/Download (e.g. "Link as RFA").
                    Called with the viewer state so callers can disable until the PDF is loaded. */}
                {actions && <span style={{marginRight: '8px'}}>{actions({disabled: loading || !pdfUrl})}</span>}
                <button className="btn btn-sm btn-secondary" onClick={printPdf} disabled={loading || !pdfUrl}
                        style={{marginRight: '8px'}}>
                    Print
                </button>
                <button className="btn btn-sm btn-secondary" onClick={downloadPdf} disabled={loading || !pdfUrl}>
                    Download PDF
                </button>
            </div>
            {loading && <div><img src={loadingGif} alt="loading"/> <span>Loading...</span></div>}
            {error && <div className="text-danger">{error}</div>}
            {pdfUrl &&
                <iframe ref={iframeRef} title={title} style={{border: "none"}}
                        width={width} height={height} src={pdfUrl}/>}
        </div>
    );
};

export default AdmissionBulkDocumentsPdf;
